import { Box, Chip, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import type { ReactNode } from "react";

interface Props {
  /** An icon, or the first letter of the name. */
  avatar: ReactNode;
  title: string;
  /** Next to the title: a badge or a status chip. */
  adornment?: ReactNode;
  /** Under the title, e.g. the username. */
  subtitle?: ReactNode;
  /** Buttons before the close button. */
  actions?: ReactNode;
  onClose: () => void;
  closeDisabled?: boolean;
}

/** The dark header of the user and client forms. */
export default function FormHeader({
  avatar,
  title,
  adornment,
  subtitle,
  actions,
  onClose,
  closeDisabled = false,
}: Props) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        bgcolor: "primary.main",
        color: "common.white",
        borderBottom: "1px solid",
        borderColor: "primary.main",
      }}
    >
      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1,
            bgcolor: "common.white",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            fontWeight: 900,
            fontSize: 14,
            color: "primary.main",
          }}
        >
          {avatar}
        </Box>
        <Box minWidth={0}>
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <Typography
              variant="subtitle2"
              fontWeight={800}
              lineHeight={1.2}
              sx={{ fontSize: 18 }}
              noWrap
            >
              {title}
            </Typography>
            {adornment}
          </Box>
          {subtitle && (
            <Typography
              variant="caption"
              component="div"
              sx={{ color: "rgba(255,255,255,0.72)" }}
              noWrap
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
        {actions}
        <IconButton
          size="small"
          aria-label="Close"
          onClick={onClose}
          disabled={closeDisabled}
          sx={{ color: "common.white" }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

/** ARCHIVED or SCHEDULED next to a title on the dark header. */
export function StatusChip({ status }: { status: "archived" | "scheduled" }) {
  return (
    <Chip
      label={status === "archived" ? "ARCHIVED" : "SCHEDULED"}
      size="small"
      sx={{
        height: 18,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.5,
        color: "common.white",
        bgcolor: status === "archived" ? "error.main" : "warning.dark",
      }}
    />
  );
}

/** The outlined button style on the dark header (Client View). */
export const HEADER_BUTTON_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 11,
  px: 1,
  minWidth: "auto",
  borderRadius: 1,
  color: "common.white",
  borderColor: "rgba(255,255,255,0.6)",
  "&:hover": { borderColor: "common.white" },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.5)",
    borderColor: "rgba(255,255,255,0.3)",
  },
} as const;
